import React from "react";
import { Link } from "react-router-dom";

/**
 * Editorial 404 / generic error page.
 *
 * Recreated from the Claude Design handoff bundle (`admin-frontend/project/404.html`):
 * a faint blueprint grid, an oversized "404" with the zero rendered as a padel-yellow
 * ball, an editorial copy column with quick links, and a mono "error log" footer.
 *
 * Wired as the router `errorElement`, so it also catches runtime/loader errors —
 * not just unknown routes.
 */

const STYLE_ID = "nf-404-styles";

const CSS = `
.nf-shell {
  --navy: #22356D;
  --navy-70: rgba(34, 53, 109, 0.7);
  --navy-50: rgba(34, 53, 109, 0.5);
  --navy-12: rgba(34, 53, 109, 0.12);
  --yellow: #EAF956;
  --bone: #FAFAF6;
  --bone-2: #F2F1EA;
  --closed-bg: #EDECE5;
  --closed-fg: #6B6A60;
  --sans: "Inter", "Inter Tight", system-ui, -apple-system, sans-serif;
  --serif: "Source Serif Pro", "Iowan Old Style", Georgia, serif;
  --mono: "JetBrains Mono", ui-monospace, "SF Mono", monospace;

  position: fixed;
  inset: 0;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background: var(--bone);
  color: var(--navy);
  font-family: var(--sans);
}

/* Faint editorial grid behind everything */
.nf-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(34, 53, 109, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(34, 53, 109, 0.05) 1px, transparent 1px);
  background-size: 88px 88px;
  background-position: -1px -1px;
  pointer-events: none;
  -webkit-mask-image: radial-gradient(ellipse 90% 70% at 50% 45%, black 40%, transparent 95%);
  mask-image: radial-gradient(ellipse 90% 70% at 50% 45%, black 40%, transparent 95%);
}

/* Top bar */
.nf-top {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 40px;
  border-bottom: 1px solid var(--navy-12);
  background: rgba(250, 250, 246, 0.7);
  -webkit-backdrop-filter: saturate(140%) blur(6px);
  backdrop-filter: saturate(140%) blur(6px);
}
.nf-logo {
  display: flex;
  align-items: baseline;
  gap: 6px;
  text-decoration: none;
  color: var(--navy);
}
.nf-logo .word {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 700;
  font-size: 26px;
  letter-spacing: -0.02em;
}
.nf-logo .amp {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  font-size: 22px;
  opacity: 0.55;
}
.nf-status {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--navy-50);
}
.nf-status .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #C2541A;
  box-shadow: 0 0 0 4px rgba(194, 84, 26, 0.12);
}

/* Main composition */
.nf-main {
  position: relative;
  z-index: 1;
  flex: 1;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 80px;
  align-items: center;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 80px 40px;
}

/* Hero number */
.nf-numwrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 0.82;
}
.nf-num {
  font-family: var(--sans);
  font-weight: 900;
  letter-spacing: -0.06em;
  font-size: clamp(220px, 32vw, 440px);
  color: var(--navy);
  display: flex;
  align-items: center;
}
.nf-num .digit { display: inline-block; }
.nf-num .zero {
  position: relative;
  width: 0.74em;
  height: 0.74em;
  margin: 0 -0.04em;
  border-radius: 50%;
  background: var(--yellow);
  box-shadow:
    inset 0 0 0 14px var(--navy),
    0 24px 60px -20px rgba(34, 53, 109, 0.35);
  transform: translateY(-0.02em) rotate(-6deg);
}
.nf-num .zero::after {
  content: "";
  position: absolute;
  top: 14px; left: 14px; right: 14px; bottom: 14px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 28%, rgba(255,255,255,0.55), transparent 55%),
    var(--yellow);
}
.nf-num .zero::before {
  content: "404";
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: var(--mono);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.32em;
  color: var(--navy);
  z-index: 2;
  transform: rotate(6deg);
  opacity: 0.7;
}

/* Mono caption beneath the number */
.nf-caption {
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--navy-50);
}
.nf-caption .rule {
  flex: 0 0 40px;
  height: 1px;
  background: var(--navy-12);
}

/* Right column copy */
.nf-copy { max-width: 460px; }
.nf-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--navy-50);
  margin-bottom: 28px;
}
.nf-eyebrow::before {
  content: "";
  width: 28px;
  height: 1px;
  background: var(--navy-50);
}

.nf-headline {
  font-family: var(--sans);
  font-weight: 800;
  color: var(--navy);
  font-size: clamp(40px, 4.6vw, 60px);
  line-height: 0.98;
  letter-spacing: -0.035em;
  margin: 0 0 22px;
  text-wrap: balance;
}
.nf-headline .italic {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.nf-headline .mark {
  display: inline-block;
  background: var(--yellow);
  color: var(--navy);
  padding: 0 0.14em;
  border-radius: 10px;
  transform: rotate(-1.5deg);
  margin: 0 0.04em;
}

.nf-lede {
  font-size: 18px;
  line-height: 1.55;
  color: var(--navy-70);
  margin: 0 0 36px;
  max-width: 44ch;
  text-wrap: pretty;
}

.nf-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 44px;
}
.nf-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 14.5px;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 160ms ease, background 160ms ease, border-color 160ms ease, color 160ms ease;
  white-space: nowrap;
  font-family: var(--sans);
}
.nf-btn:hover { transform: translateY(-1px); }
.nf-btn:active { transform: translateY(0); }
.nf-btn-primary {
  background: var(--navy);
  color: #fff;
}
.nf-btn-primary:hover { background: #0e1a45; }
.nf-btn-ghost {
  background: transparent;
  color: var(--navy);
  border-color: var(--navy-12);
}
.nf-btn-ghost:hover { background: var(--bone-2); }
.nf-btn-primary svg { transition: transform 200ms ease; }
.nf-btn-primary:hover svg { transform: translateX(3px); }

/* Helpful links list */
.nf-links {
  border-top: 1px solid var(--navy-12);
  padding-top: 22px;
}
.nf-links-label {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--navy-50);
  margin-bottom: 14px;
}
.nf-link-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--navy-12);
  color: var(--navy);
  text-decoration: none;
  transition: padding 160ms ease;
}
.nf-link-row:last-child { border-bottom: none; }
.nf-link-row:hover { padding-left: 6px; }
.nf-link-row .label {
  font-size: 15px;
  font-weight: 600;
}
.nf-link-row .sub {
  font-size: 13px;
  color: var(--navy-50);
  margin-left: 12px;
  flex: 1;
}
.nf-link-row .arr {
  width: 18px;
  height: 18px;
  color: var(--navy-50);
  transition: color 160ms ease, transform 160ms ease;
}
.nf-link-row:hover .arr {
  color: var(--navy);
  transform: translateX(3px);
}

/* Bottom bar — error log */
.nf-log {
  position: relative;
  z-index: 2;
  border-top: 1px solid var(--navy-12);
  background: var(--bone-2);
  padding: 14px 40px;
  display: flex;
  align-items: center;
  gap: 18px;
  font-family: var(--mono);
  font-size: 12px;
  color: var(--navy-70);
  flex-wrap: wrap;
}
.nf-log .tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--closed-bg);
  color: var(--closed-fg);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.nf-log .path {
  color: var(--navy);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.nf-log .ts { color: var(--navy-50); }
.nf-log .id { color: var(--navy-50); }

/* Responsive */
@media (max-width: 980px) {
  .nf-main {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 56px 28px;
  }
  .nf-num { font-size: clamp(180px, 48vw, 320px); }
  .nf-top { padding: 18px 24px; }
  .nf-log { padding: 14px 24px; font-size: 11px; }
  .nf-log .path { width: 100%; flex: none; }
}
`;

const Arrow = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const ErrorElement = () => {
  // Inject the page styles once.
  React.useLayoutEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const tag = document.createElement("style");
    tag.id = STYLE_ID;
    tag.textContent = CSS;
    document.head.appendChild(tag);
  }, []);

  // Build a request-log line so the footer feels like a real error log.
  const log = React.useMemo(() => {
    let path = window.location.pathname + window.location.search;
    if (!path || path === "/" || path === "/404.html") {
      path = "/" + (window.location.hash ? window.location.hash.slice(1) : "missing-resource");
    }
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const ts =
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const id =
      "req_" +
      Math.random().toString(36).slice(2, 10) +
      Math.random().toString(36).slice(2, 6);
    return { path, ts, id };
  }, []);

  return (
    <div className="nf-shell">
      <div className="nf-grid" aria-hidden="true" />

      <header className="nf-top">
        <Link to="/" className="nf-logo" aria-label="Book & Go home">
          <span className="word">Book</span>
          <span className="amp">&amp;</span>
          <span className="word">Go</span>
        </Link>
        <div className="nf-status">
          <span className="dot" />
          <span>Error 404 · Page not found</span>
        </div>
      </header>

      <main className="nf-main">
        <div className="nf-numwrap">
          <div className="nf-num" aria-label="404">
            <span className="digit">4</span>
            <span className="zero" role="img" aria-label="0" />
            <span className="digit">4</span>
          </div>
          <div className="nf-caption">
            <span className="rule" />
            <span>HTTP · Not Found</span>
          </div>
        </div>

        <div className="nf-copy">
          <div className="nf-eyebrow">Something&apos;s off the schedule</div>
          <h1 className="nf-headline">
            We couldn&apos;t <span className="italic">find</span> what you were{" "}
            <span className="mark">looking for.</span>
          </h1>
          <p className="nf-lede">
            The page may have moved, the link might be old, or the URL was
            mistyped. Either way — let&apos;s get you back on the booking floor.
          </p>

          <div className="nf-actions">
            <Link to="/calendars" className="nf-btn nf-btn-primary">
              Back to dashboard
              <Arrow />
            </Link>
            <button
              type="button"
              className="nf-btn nf-btn-ghost"
              onClick={() => window.history.back()}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M11 5l-7 7 7 7" />
              </svg>
              Go back
            </button>
          </div>

          <div className="nf-links">
            <div className="nf-links-label">Or jump to</div>
            <Link className="nf-link-row" to="/calendars">
              <span className="label">Calendar</span>
              <span className="sub">Today&apos;s bookings &amp; lessons</span>
              <Arrow className="arr" />
            </Link>
            <Link className="nf-link-row" to="/payments">
              <span className="label">Payments</span>
              <span className="sub">Recent transactions &amp; refunds</span>
              <Arrow className="arr" />
            </Link>
            <Link className="nf-link-row" to="/customers">
              <span className="label">Customers</span>
              <span className="sub">Profiles, memberships, history</span>
              <Arrow className="arr" />
            </Link>
            <Link className="nf-link-row" to="/support">
              <span className="label">Contact support</span>
              <span className="sub">We usually reply within an hour</span>
              <Arrow className="arr" />
            </Link>
          </div>
        </div>
      </main>

      <footer className="nf-log" aria-label="Error details">
        <span className="tag">Log</span>
        <span className="path">GET&nbsp; {log.path}</span>
        <span className="ts">{log.ts}</span>
        <span className="id">{log.id}</span>
      </footer>
    </div>
  );
};

export default ErrorElement;
